/**
 * The sentences the "who are they" panel is allowed to write.
 *
 * There are only two, they are both arithmetic, and that is deliberate. The
 * panel's whole claim is that everything on it comes off a filing, so any
 * prose it produces has to be a rendering of a number rather than a
 * statement about an organisation — every clause below is a count, a share
 * or a name already in the payload, and none of them survives being wrong
 * about the data.
 *
 * Kept out of the component file so fast refresh still works there, and so
 * the wording can be tested without rendering anything.
 */

import type { Profile, ProfileSubject } from "../api";

/** "A", "A and B", "A, B and C" — an Oxford-comma-free English list. */
export function joined(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** How much of the disclosed activity the leading subjects account for.
 *
 *  Enough codes to cover this much of it, so the sentence is about a
 *  concentration rather than about an ordering. A firm whose top three
 *  codes are 12% of its filings has no leading subject, and saying it does
 *  would be the most misleading line on the page. */
const COVERS = 0.6;

/** At most this many named, however thinly the activity is spread. Past
 *  three the sentence stops being a summary and becomes the list that sits
 *  underneath it anyway. */
const MOST = 3;

/**
 * What they lobby about, as one sentence, or nothing.
 *
 * Nothing in three cases, all of them real: no activity on file, and the
 * two shapes where the sentence would say less than the bars below it.
 */
export function concentration(profile: Pick<Profile, "subjects" | "activities">): string | null {
  const { subjects, activities } = profile;
  if (!activities || subjects.length === 0) return null;

  const filings = `${activities.toLocaleString()} ${activities === 1 ? "activity" : "activities"}`;
  if (subjects.length === 1) {
    return `Every one of the ${filings} they have disclosed is filed under ${subjects[0].label}.`;
  }

  // A set of subjects may only be called the leading ones if the last of
  // them is strictly ahead of the next — otherwise the sentence is picking
  // between equals on nothing. Amazon files one omnibus LD-2 a quarter
  // naming every code it touches, so its distribution is exactly flat, and
  // the panel read the first three off an alphabetical tie and called them
  // the largest, which had Amazon lobbying about aviation.
  //
  // Cutting at the tie is not enough either: AIPAC has three subjects at a
  // third each and a fourth at 2%, where the honest sentence names all
  // three. So the list grows to the last position that is a real boundary,
  // and stops at the first boundary past the share worth reporting.
  let leading: ProfileSubject[] = [];
  let covered = 0;
  let running = 0;
  for (let at = 0; at < subjects.length && at < MOST; at++) {
    running += subjects[at].share;
    const ahead = at + 1 >= subjects.length || subjects[at].share > subjects[at + 1].share;
    if (!ahead) continue;
    leading = subjects.slice(0, at + 1);
    covered = running;
    if (covered >= COVERS) break;
  }

  // Nothing is ahead of anything: not a gap in the record but a fact about
  // the filer, and the only honest sentence left is the shape of it.
  if (leading.length === 0) {
    const widest = Math.round(subjects[0].share * 100);
    const narrowest = Math.round(subjects[subjects.length - 1].share * 100);
    const even =
      widest === narrowest
        ? `at about ${widest}% each`
        : `each between ${narrowest}% and ${widest}% of them`;
    return `Their ${filings} are spread across ${subjects.length} subjects with none of them ahead of the rest, ${even}.`;
  }
  // Every code named and still nowhere near a majority: the activity is
  // genuinely spread, which is a fact about the firm and not a gap. Said
  // that way round rather than dressed up as a leading subject.
  if (leading.length === subjects.length && covered < COVERS) {
    return `Their ${filings} are spread across all ${subjects.length} subjects below, with no one of them accounting for more than ${Math.round(subjects[0].share * 100)}%.`;
  }
  // Singular where the boundary falls after one subject, which it does
  // whenever a filer has one clear leader over an even tail.
  const named = joined(leading.map((s) => s.label));
  const verb = leading.length === 1 ? "accounts" : "account";
  if (covered < COVERS) {
    const largest =
      leading.length === 1 ? "the largest of which" : `the largest ${leading.length} of which`;
    return `Their ${filings} are spread across ${subjects.length} subjects, ${largest} — ${named} — ${verb} for ${Math.round(covered * 100)}% of them.`;
  }
  return `${named} ${verb} for ${Math.round(covered * 100)}% of the ${filings} they have disclosed.`;
}

/**
 * Who pays them to lobby, as one sentence.
 *
 * The line that separates a membership organisation from a firm, and it is
 * a boolean in the payload. AIPAC is the client on every one of its own
 * filings: nobody pays it to lobby, which is not a gap in the record and is
 * the most informative single thing here about what it is.
 */
export function whoPays(profile: Profile): string | null {
  if (profile.kind === "committee") return null;
  if (profile.self_funded) {
    return "They lobby for themselves. On every filing held here the client is the organisation itself, so no outside client is paying for this — which is what separates a membership organisation from the firms beside it on the index.";
  }
  if (profile.funders_total === 0) return null;
  const clients =
    profile.funders_total === 1
      ? "one client"
      : `${profile.funders_total.toLocaleString()} clients`;
  return `They lobby on behalf of ${clients}, who are who pays for it. A lobbying firm's clients are the substance of what it is, and each of them holds their own view rather than the firm holding one.`;
}

/**
 * Why the quote above came off a quarterly report and not the register.
 *
 * The panel quotes the register's own description where there is one, and
 * two thirds of the organisations that give money wrote one. The other
 * third is not the small third — the Realtors, the teachers' union, Amazon,
 * Comcast, Lockheed and Chevron are all in it — and for them the panel
 * falls back to the free text on a quarterly LD-2.
 *
 * A reader who saw the register quoted on the last page needs to know why
 * this one is different, and the register's gap is worth stating on its own
 * account: it is the filer's, not ours. What this must not do is run
 * alongside the old "nothing has been substituted for it", which was true
 * when nothing was and is a small lie beside a substitute.
 */
export function insteadOfTheRegister(profile: Pick<Profile, "refused">): string {
  const instead = "so this is quoted from what they file every quarter instead";
  if (profile.refused === "blank") {
    return `Their entry on the register leaves the description field blank, ${instead}.`;
  }
  if (!profile.refused) {
    return `The register's own entry for them has not been read yet, ${instead}. It arrives with the next sweep of the register.`;
  }
  return `Their entry on the register holds ${profile.refused} in the description field rather than a description of what they do, ${instead}.`;
}

/** The bench, and how much of it is a career on the Hill.
 *
 *  Counted rather than asserted. "Three of this firm's lobbyists previously
 *  staffed members of Congress" is a strong sentence and it is only worth
 *  making when the number behind it is on the page beside it. */
export function bench(profile: Profile): { revolved: number; tied: number } {
  const revolved = profile.people.filter((p) => p.position !== null).length;
  const tied = profile.people.filter((p) => p.members.length > 0).length;
  return { revolved, tied };
}

/**
 * How many of the named people came out of a government job.
 *
 * A separate function for one clause because the clause has to survive a
 * bench of one, which "1 of the 1 people" does not. Firms with a single
 * named lobbyist are the majority of the register.
 */
export function benchSentence(revolved: number, total: number): string {
  const post = "worked in government before lobbying";
  if (total === 1) return `The one person named on these filings ${post}`;
  if (revolved === total)
    return `All ${total.toLocaleString()} people named on these filings ${post}`;
  return `${revolved.toLocaleString()} of the ${total.toLocaleString()} people named on these filings ${post}`;
}
